'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';
import { useAuth } from '@/contexts/AuthContext';
import Link from 'next/link';

interface LoginFormData {
  email: string;
  password: string;
}

const networkStats = [
  { label: 'Partenaires actifs', value: '247' },
  { label: 'Prospects ce mois', value: '1 842' },
  { label: 'Devis signés', value: '312' },
];

function resolveRoleFromUser(user: any): 'admin' | 'partner' {
  const role =
    user?.user_metadata?.role ||
    user?.app_metadata?.role ||
    'partner';
  return role === 'admin' ? 'admin' : 'partner';
}

export default function LoginScreen() {
  const router = useRouter();
  const { signIn } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);

  const { register, handleSubmit, formState: { errors } } = useForm<LoginFormData>({
    defaultValues: { email: '', password: '' },
  });

  const onSubmit = async (data: LoginFormData) => {
    setLoading(true);
    setAuthError(null);
    try {
      const result = await signIn(data.email, data.password);
      if (result?.session) {
        const role = resolveRoleFromUser(result.user);
        console.log('[AUTH DIAG] redirect started');
        const destination = role === 'admin' ? '/admin-dashboard' : '/partner-dashboard';
        console.log('[AUTH DIAG] redirect destination:', destination);
        router.push(destination);
        console.log('[AUTH DIAG] redirect completed');
      } else {
        setAuthError('Identifiants incorrects. Veuillez vérifier votre e-mail et votre mot de passe.');
      }
    } catch (error: any) {
      setAuthError('Identifiants incorrects. Veuillez vérifier votre e-mail et votre mot de passe.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left panel */}
      <div className="hidden lg:flex flex-col w-[520px] flex-shrink-0 bg-card border-r border-border relative overflow-hidden">
        <div className="absolute inset-0 bg-red-gradient opacity-5" />
        <div className="absolute top-0 left-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-0 right-0 w-64 h-64 bg-primary/8 rounded-full blur-3xl translate-x-1/3 translate-y-1/3" />

        <div className="relative z-10 flex flex-col h-full p-10">
          {/* Logo */}
          <div className="flex items-center gap-3">
            <AppLogo size={40} />
            <div>
              <span className="text-lg font-bold text-foreground">Mon Plan Rénov</span>
              <p className="text-xs text-muted-foreground">Espace Partenaires</p>
            </div>
          </div>

          {/* Hero */}
          <div className="flex-1 flex flex-col justify-center">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary/10 border border-primary/20 rounded-full mb-6 w-fit">
              <span className="w-1.5 h-1.5 rounded-full bg-primary status-dot-live" />
              <span className="text-xs font-medium text-primary">Réseau actif en temps réel</span>
            </div>
            <h1 className="text-4xl font-bold text-foreground leading-tight mb-4">
              Pilotez votre<br />
              <span className="text-gradient-red">partenariat</span><br />
              en direct.
            </h1>
            <p className="text-muted-foreground text-base leading-relaxed max-w-sm">
              Suivez chaque scan, chaque prospect, chaque commission. Votre activité, transparente et en temps réel.
            </p>

            {/* Stats */}
            <div className="mt-10 grid grid-cols-3 gap-4">
              {networkStats.map((stat) => (
                <div key={`stat-${stat.label}`} className="bg-muted rounded-xl p-4 border border-border">
                  <p className="text-2xl font-bold text-foreground font-tabular">{stat.value}</p>
                  <p className="text-xs text-muted-foreground mt-1">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Footer */}
          <p className="text-xs text-muted-foreground">© 2026 Mon Plan Rénov · Tous droits réservés</p>
        </div>
      </div>

      {/* Right panel — form */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md fade-in">
          {/* Mobile logo */}
          <div className="flex items-center gap-3 mb-8 lg:hidden">
            <AppLogo size={36} />
            <span className="text-base font-bold text-foreground">Mon Plan Rénov</span>
          </div>

          <div className="mb-8">
            <h2 className="text-2xl font-bold text-foreground mb-2">Connexion</h2>
            <p className="text-muted-foreground text-sm">Accédez à votre espace partenaire sécurisé.</p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5" noValidate>
            {/* Auth error */}
            {authError && (
              <div className="flex items-start gap-3 bg-primary/10 border border-primary/20 rounded-lg px-4 py-3">
                <Icon name="ExclamationCircleIcon" size={18} className="text-primary flex-shrink-0 mt-0.5" />
                <p className="text-sm text-primary">{authError}</p>
              </div>
            )}

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-foreground mb-1.5">
                Adresse e-mail
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                placeholder="votre@email.fr"
                suppressHydrationWarning
                className={`w-full bg-input border rounded-lg px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all ${errors.email ? 'border-primary' : 'border-border'}`}
                {...register('email', {
                  required: "L'adresse e-mail est requise.",
                  pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: "Format d'e-mail invalide." },
                })}
              />
              {errors.email && <p className="mt-1.5 text-xs text-primary">{errors.email.message}</p>}
            </div>

            {/* Password */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-foreground mb-1.5">
                Mot de passe
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  placeholder="••••••••"
                  suppressHydrationWarning
                  className={`w-full bg-input border rounded-lg px-4 py-3 pr-12 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all ${errors.password ? 'border-primary' : 'border-border'}`}
                  {...register('password', {
                    required: 'Le mot de passe est requis.',
                    minLength: { value: 6, message: 'Minimum 6 caractères.' },
                  })}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors p-1"
                  aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                  suppressHydrationWarning
                >
                  <Icon name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={18} />
                </button>
              </div>
              {errors.password && <p className="mt-1.5 text-xs text-primary">{errors.password.message}</p>}
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              suppressHydrationWarning
              className="w-full bg-primary hover:bg-accent text-primary-foreground font-semibold py-3 px-6 rounded-lg transition-all duration-150 active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm"
            >
              {loading ? (
                <>
                  <Icon name="ArrowPathIcon" size={16} className="animate-spin" />
                  <span>Connexion en cours…</span>
                </>
              ) : (
                'Se connecter'
              )}
            </button>
          </form>

          <p className="mt-6 text-center text-xs text-muted-foreground">
            Pas encore partenaire ?{' '}
            <span className="text-primary hover:text-accent cursor-pointer transition-colors">Contactez-nous</span>
          </p>

          <div className="mt-6 pt-6 border-t border-border text-center">
            <Link
              href="/admin-login"
              className="inline-flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              <Icon name="ShieldCheckIcon" size={14} />
              <span>Connexion Administrateur</span>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}